import React, { useCallback, useEffect } from 'react';
import { useAutoResize } from './ChatContext';
import { trans } from '../utils';

/**
 * Chat Input - Text input and send controls
 */
const ChatInput = ({
  value,
  onChange,
  onSend,
  onAttachContext,
  attachedContext,
  onRemoveContext,
  isLoading,
  hasContext
}) => {
  const { textareaRef } = useAutoResize(value);

  // Focus the input when loading completes (response received)
  useEffect(() => {
    if (!isLoading && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [isLoading, textareaRef]);

  const handleKeyDown = useCallback((e) => {
    // Send on Enter (without Shift)
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (value.trim() && !isLoading) {
        onSend(value);
      }
    }
  }, [value, isLoading, onSend]);

  const handleSendClick = useCallback(() => {
    if (value.trim() && !isLoading) {
      onSend(value);
    }
  }, [value, isLoading, onSend]);

  const isDisabled = !value.trim() || isLoading;

  return (
    <div className="chat-input-container">
      {/* Attach Context Button */}
      <button
        className={`chat-attach-button ${attachedContext ? 'is-attached' : ''}`}
        onClick={onAttachContext}
        disabled={!hasContext}
        aria-label={attachedContext ? trans('Context attached') : trans('Attach current view')}
        title={hasContext ? trans('Attach current view') : trans('No context available')}
      >
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"></path>
        </svg>
      </button>

      {/* Input Wrapper */}
      <div className="chat-input-wrapper">
        {/* Attached Context Pill */}
        {attachedContext && (
          <div className="chat-attached-context">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"></path>
            </svg>
            <span>
              {attachedContext.description?.length > 25 
                ? attachedContext.description.substring(0, 25) + '...' 
                : attachedContext.description || trans('Current view')}
            </span>
            <button onClick={onRemoveContext} aria-label={trans('Remove context')}>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>
        )}

        {/* Text Input */}
        <textarea
          ref={textareaRef}
          className="chat-text-input"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={trans('Ask about your data...')}
          aria-label={trans('Type your message')}
          rows={1}
          disabled={isLoading}
        />
      </div>

      {/* Send Button */}
      <button
        className="chat-send-button"
        onClick={handleSendClick}
        disabled={isDisabled}
        aria-label={trans('Send message')}
      >
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="22" y1="2" x2="11" y2="13"></line>
          <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
        </svg>
      </button>
    </div>
  );
};

export default ChatInput;
